/* eslint-disable max-lines */
/* eslint-disable import/max-dependencies */
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import TuneIcon from '@mui/icons-material/Tune';
import { Pagination as IMPagination } from '@im/ui-lib/Pagination';
import { useTranslate } from '@im/react-localization';
import DiscountLinesSearchBar from '../../SearchBar/SearchDiscountLines';
import { LineDetails, useDealDetailsQuery } from '../../../hooks/deal-details';
import TableSetting from '../../TableSettings/TableSetting';
import QuantityValidationDialog from './QuantityValidationDialog';
import {
  initialPageSetup,
  useGenerateQuote,
  useRefreshImSku,
  useRefreshImSkuForRulesEngine,
} from './api';
import Typography from '@mui/material/Typography';
import ConfirmationDialog from 'src/components/ConfirmationDialog/ConfirmationDialog';
import {
  useCollasableRows,
  useDiscountLineColumns,
  useSelectColumns,
  useSortedGroups,
  usePinnedColumns,
} from './columns';
import { useDownloadOrderHistory } from 'src/views/vendorprogram/hooks/order-history';
import SyncIcon from '@mui/icons-material/Sync';
import { downloadFile } from 'src/util/downloadFileUtil';
import useSnackBar from 'src/hooks/useSnackBar';
import ShowBCNListDialog from '../../BCNListDialogNew';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useReAcquireDeals } from 'src/views/vendorprogram/hooks/splitAndRemove';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useSaveIncentiveSelection,
  UpdateBtlIncentiveSelectionRequest,
} from 'src/views/vendorprogram/hooks/exceptionDeal';
import Table from './Table';

interface DealDetailsPageTableProps {
  // For test injection
  _testBaseSalesValues?: Record<string, boolean>;
  _testProficiencyValues?: Record<string, boolean>;
  allowDownloadOrderHistory?: boolean;
  allowQuoteGeneration?: boolean;
  companyCode: string;
  customerset_id: number;
  dealStatus?: string;
  dealid: number;
  disableQtyEdit: boolean;
  exception: boolean;
  handleRowClick?: (row: LineDetails) => void;
  importreferenceid: number;
  openPopup?: string;
  vendorProgramId: number;
}

export default function DealDetailsPageTable({
  _testBaseSalesValues,
  _testProficiencyValues,
  allowQuoteGeneration,
  companyCode,
  customerset_id,
  dealStatus,
  dealid,
  disableQtyEdit,
  exception,
  handleRowClick,
  importreferenceid,
}: Readonly<DealDetailsPageTableProps>) {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  const businessType = searchParams.get('businessType');
  const isPurged = searchParams.get('isPurged')?.trim() === 'true';
  const [quantityError, setQuantityError] = useState<any>(false);
  const [openTableSettings, setOpenTableSettings] = useState<boolean>(false);
  const [openPopup, setOpenPopup] = useState<
    'bcnList' | 'saveIncentive' | false
  >(false);
  const [vpn, setVpn] = useState<string>('');
  const [showPennyCostLines, setShowPennyCostLines] = useState<boolean>(false);
  const [opensaveincentivebtn, setOpensaveincentivebtn] =
    useState<boolean>(false);
  // ...existing code...
  const [pagination, setPagination] = useState<{
    page: number;
    pageSize: number;
  }>(initialPageSetup);

  const { data, isLoading } = useDealDetailsQuery({
    CUSTOMERSET_ID: customerset_id,
    dealid,
    importreferenceid,
    isPennyCost: showPennyCostLines,
    vpn_prefix: vpn,
    ...pagination,
    businessType: businessType,
    isPurged,
  });

  const {
    column_groups: columnGroups_,
    deal_id: dealId,
    deal_number: dealNumber,
    isXcmVendor: xcmVendor,
    lines,
    lines_total_pages,
    treeView_lines_total_count: totalCount,
    vendor_id: vendorId,
    vendor_program_id: vendorProgramId,
  } = data ?? {
    column_groups: [],
    deal_id: 0,
    deal_number: '',
    isXcmVendor: true,
    lines: [],
    lines_total_pages: 0,
    treeView_lines_total_count: 0,
    vendor_id: 0,
    vendor_program_id: 0,
  };
  const columnGroups = columnGroups_.map((c) => ({ ...c, key: c.name }));
  const { setSortableList, sortedColumnGroups } = useSortedGroups(columnGroups);

  const allowSelectAll = totalCount === lines.length;

  const { onExpand, rows } = useCollasableRows(lines);
  const isLinesHasBundle = lines.some(
    (l) => l.childDiscountLines && l.childDiscountLines?.length > 0
  );
  // Track ON/OFF toggle values for each row (true = On, false = Off)
  // Separate state for baseSales and proficiency toggles
  const [baseSalesValues, setBaseSalesValues] = useState<
    Record<string, boolean>
  >(_testBaseSalesValues ?? {});
  const [proficiencyValues, setProficiencyValues] = useState<
    Record<string, boolean>
  >(_testProficiencyValues ?? {});

  const [masterLines, setMasterLines] = useState<Record<number, LineDetails>>(
    {}
  );

  const { columns, errors, lineQuantities, selected, setUpAllSelected } =
    useSelectColumns({
      disableQtyEdit,
      enableSelectAll: allowSelectAll,
      isBidActive: data?.bidstatus === 'A',
      isLineIdAllowed: !!data?.isLineIdAllowed,
      isSplitAndRemoveAllowed: !!data?.isSplitAndRemoveAllowed,
      onError: setQuantityError,
      onExpandClick: onExpand,
      rawLines: lines,
      showCollapsCol: isLinesHasBundle,
      showQuantityCol: allowQuoteGeneration,
    });

  const disableQuoteGeneration = !!Object.keys(errors).length;
  const discountTrackingId = selected
    .map((s) => lines.find((l) => l.item_id === s))
    .filter((l) => !!l)
    .flatMap((line) => [
      line?.dd_tracking,
      line?.childDiscountLines?.map((c) => c.dd_tracking),
    ])
    .join(', ');
  const downloadOrderHistory = useDownloadOrderHistory({
    discountTrackingId,
    page: 1,
    pageSize: 10,
  });
  const { isLoading: mutationPending, mutateAsync: generateQuoteAsync } =
    useGenerateQuote({
      dealId,
      dealNumber,
      discountType: data?.discounttype,
      lines: Object.values(masterLines),
      vendorId,
      vendorProgramId,
    });
  function generateQuote(bcnList: string | null) {
    generateQuoteAsync({
      bcnList: bcnList,
      bidItemList: lineQuantities,
      isFullQuote: !selected.length,
      resellerName: data?.reseller_name ?? null,
    })
      .then(() => {
        showSnackBar({
          autoHide: 2000,
          message: translate('VendorDeals.autoQuoteQueued'),
          severity: 'success',
        });
        setUpAllSelected(false);
        setOpenPopup(false);
      })
      .catch((e) =>
        showSnackBar({
          autoHide: 2000,
          message: e.toString(),
          severity: 'error',
        })
      );
  }

  const discountColumns = useDiscountLineColumns(
    sortedColumnGroups,
    importreferenceid,
    xcmVendor,
    data?.vendor_program_id ?? 0,
    baseSalesValues,
    setBaseSalesValues,
    proficiencyValues,
    setProficiencyValues,
    exception && data?.showIncentivesHeader,
    data?.version ?? 0,
    data?.enableRulesEngine ?? false,
    data?.productLocalizationForWeb ?? ''
  );

  const spliteAndRemoveColumnIndex = 1;
  const quantityColumnIndex = 6;
  const renderedColumns = [
    ...(columns.collapsColumn ? [columns.collapsColumn] : []),
    ...(columns.checkboxColumn ? [columns.checkboxColumn] : []),
    ...(columns.lineIdColumn ? [columns.lineIdColumn] : []),
    ...discountColumns.slice(0, spliteAndRemoveColumnIndex),
    ...(columns.spliteAndRemoveColumn ? [columns.spliteAndRemoveColumn] : []),
    ...discountColumns.slice(spliteAndRemoveColumnIndex, quantityColumnIndex),
    ...(columns.quantityColumn ? [columns.quantityColumn] : []),
    ...discountColumns.slice(quantityColumnIndex),
  ];

  useEffect(() => {
    if (lines) {
      setMasterLines((prev) => {
        const updated = { ...prev };
        lines.forEach((line) => {
          updated[line.item_id] = line;
        });
        return updated;
      });
    }
  }, [lines]);

  const requestPagination = () => (
    <IMPagination
      data-testid="gds-vp-upload-queue-table-pagination"
      onPageChange={(page) => setPagination({ ...pagination, page })}
      onRowsPerPageChange={(pageSize) =>
        setPagination({ ...pagination, pageSize })
      }
      page={pagination.page}
      rowsPerPage={pagination.pageSize}
      totalCount={totalCount}
    />
  );

  const handleDownloadOrderHistory = () => {
    showSnackBar({
      autoHide: 'stay',
      message: translate('Common.Downloading'),
      severity: 'info',
    });
    downloadOrderHistory
      .mutateAsync()
      .then((res) => {
        downloadFile(res, `OrderHistory-${dealid}.xlsx`);
        showSnackBar({
          autoHide: 2000,
          message: translate('Common.DownloadSuccess'),
          severity: 'success',
        });
      })
      .catch(() => {
        showSnackBar({
          autoHide: 2000,
          message: translate('Common.FileNotFound'),
          severity: 'error',
        });
      });
  };
  const refreshImSku = useRefreshImSku(
    {
      company: companyCode,
      isXcmVendor: xcmVendor,
      lineItems: lines
        .filter(
          (val) =>
            val.im_sku === '' || val.im_sku === null || val.im_sku === undefined
        )
        .map((val) => ({
          trackingNumber: String(val.dd_tracking),
          vendorCode: val.vendor_number,
          vpn: val.vpn,
        })),
    },
    importreferenceid
  );

  const refreshImSkuForRulesEngine = useRefreshImSkuForRulesEngine(
    importreferenceid,
    {
      bidNumber: dealNumber,
      productLines: lines
        .filter(
          (val) =>
            val.im_sku === '' || val.im_sku === null || val.im_sku === undefined
        )
        .map((val) => ({
          additionalAttributes: val.additional_attributes,
          discountDetailId: val.discount_detail_id,
          discountTrackingId: val.dd_tracking,
          productBusinessType: val.product_business_type,
          productSetType: val.productset_type,
          sbo2ProductSetId: String(val.sbo2_product_set_Id),
          vpn: val.vpn,
        })),
      productLocalizationForWeb: data?.productLocalizationForWeb ?? '',
      skSbo: data?.deal_id ?? '',
      vendor: data?.vendor_name ?? '',
      vendorProgramId: data?.vendor_program_id?.toString() ?? '',
      vendorProgramName: data?.vendor_program_name ?? '',
    }
  );

  const handleRefreshImSku = () => {
    showSnackBar({
      autoHide: 2000,
      message: translate('VendorProgram.RefreshImSkuSuccess'),
      severity: 'success',
    });
    if (!data?.enableRulesEngine) {
      refreshImSku.mutateAsync().catch(() => {
        showSnackBar({
          autoHide: 2000,
          message: translate('VendorProgram.RefreshImSkuFailure'),
          severity: 'error',
        });
      });
    } else {
      refreshImSkuForRulesEngine.mutateAsync().catch(() => {
        showSnackBar({
          autoHide: 2000,
          message: translate('VendorProgram.RefreshImSkuForRulesEngineFailure'),
          severity: 'error',
        });
      });
    }
  };
  // Use the mutation hook, passing importreferenceid only
  const updateLine = useSaveIncentiveSelection(String(importreferenceid));
  const onSaveClick = (payload: UpdateBtlIncentiveSelectionRequest[]) => {
    // Compose the payload for the API as per required structure
    if (!payload.length) return;
    updateLine
      .mutateAsync(payload[0])
      .then((response: { transactionID?: string }) => {
        setOpenPopup(false);
        setOpensaveincentivebtn(false);
        showSnackBar({
          message: translate('ExceptionDeals.UpdateIncentiveUSuccess', {
            value: response?.transactionID ?? '',
          }),
          severity: 'success',
        });
      })
      .catch((error: { response?: { message?: string } }) => {
        setOpensaveincentivebtn(false);
        setOpenPopup(false);
        showSnackBar({
          message:
            error?.response?.message ||
            translate('ExceptionDeals.UpdateIncentiveUFailure'),
          severity: 'error',
        });
      });
  };

  const handleSaveIncentive = () => {
    let lineBtlIncentives = [];
    setOpensaveincentivebtn(true);
    setOpenPopup(false);
    showSnackBar({
      autoHide: 'stay',
      message: translate('ExceptionDeals.UpdateWaitingMessage'),
      severity: 'info',
    });
    const allRows = Object.values(masterLines);

    const firstIncentiveValue = allRows[0];
    const defaultIncentive = {
      additional_attributes:
        typeof firstIncentiveValue.additional_attributes === 'string'
          ? firstIncentiveValue.additional_attributes
          : '',
      baseSales: !!baseSalesValues[String(firstIncentiveValue.item_id)],
      discountDetailLineId: firstIncentiveValue.discount_detail_id,
      productSetLineId: firstIncentiveValue.item_id,
      proficiency: !!proficiencyValues[String(firstIncentiveValue.item_id)],
      trackingID: firstIncentiveValue.dd_tracking,
    };

    const selectedIncentives = allRows
      .filter(
        (row, index) =>
          !!baseSalesValues[String(row.item_id)] ||
          !!proficiencyValues[String(row.item_id)]
      )
      .map((row) => ({
        additional_attributes:
          typeof row.additional_attributes === 'string'
            ? row.additional_attributes
            : '',
        baseSales: !!baseSalesValues[String(row.item_id)],
        discountDetailLineId: row.discount_detail_id,
        productSetLineId: row.item_id,
        proficiency: !!proficiencyValues[String(row.item_id)],
        trackingID: row.dd_tracking,
      }));

    if (selectedIncentives.length !== 0) {
      lineBtlIncentives = [...selectedIncentives];
    } else {
      lineBtlIncentives = [defaultIncentive, ...selectedIncentives];
    }
    const payload: UpdateBtlIncentiveSelectionRequest = {
      dealnumber: dealNumber,
      lineBtlIncentives,
      skSbo: dealid,
      vendorprogramid: vendorProgramId,
    };

    // ALWAYS submit
    onSaveClick([payload]);
  };

  const handleGenerateQuote = (selectedBcn: string[], disabled: boolean) => {
    const bcnList = selectedBcn.join(',') || (data?.reseller_number as string);
    return (
      <LoadingButton
        data-testid="vp-generate-full-quote"
        disabled={disableQuoteGeneration || disabled || !bcnList}
        loading={mutationPending}
        onClick={() => generateQuote(bcnList)}
        variant="newContained"
      >
        {selected.length
          ? translate('VendorDeals.generateQuote', {
              count: selected.length,
            })
          : translate('VendorDeals.generateFullQuote')}
      </LoadingButton>
    );
  };

  const pinnedColumnsStyle = usePinnedColumns(
    renderedColumns.slice(1),
    {
      left: [
        isLinesHasBundle ? '__COLLAPSABLE__' : '',
        GRID_CHECKBOX_SELECTION_COL_DEF.field,
        'line_id',
        'vpn',
        'spliteAndRemove',
        'item_id',
        'dd_tracking',
      ],
    },
    lines.length > 0 &&
      _.isEqual(
        sortedColumnGroups.map((c) => c.key),
        columnGroups.map((c) => c.key)
      )
  );
  const reAcquireDeals = useReAcquireDeals({
    CompanyCode: companyCode,
    CountryCode: countryCode ?? '',
    Dealid: dealNumber,
    HeaderId: data?.manaualHeaderId?.toString() ?? '',
    VendorName: data?.vendor_name ?? '',
    VendorProgramId: data?.vendor_program_id?.toString() ?? '',
    VendorProgramName: data?.vendor_program_name ?? '',
  });

  const onReAcquireClick = () => {
    reAcquireDeals
      .mutateAsync()
      .then((response) => {
        if (response.isAcquireQuoteApiSuccessful) {
          showSnackBar({
            message: translate('VendorDeals.DealReAcquiredMessage'),
            severity: 'success',
          });
          navigate(
            `/vendor-program/search-results?dealNumber=${dealNumber}&countryCode=${countryCode}&companyCode=${companyCode}`
          );
        } else {
          showSnackBar({
            message: response.message,
            severity: 'error',
          });
        }
      })
      ?.catch(() => {
        showSnackBar({
          message: translate('Common.Error'),
          severity: 'error',
        });
      });
  };

  return (
    <>
      <Stack mt={2}>
        <Stack
          alignContent="center"
          direction="row"
          justifyContent="flex-end"
          mb={1}
        >
          <Stack direction="row" spacing={1}>
            {businessType !== 'Subscription' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showPennyCostLines}
                    data-testid="gds-vp-ss-penny-cost-lines"
                    onChange={(e) => setShowPennyCostLines(e.target.checked)}
                  />
                }
                label={translate('VendorDeals.FilterPennyCostLines')}
                sx={{ gap: 0.5, margin: 0 }}
              />
            )}
            {data?.isSplitAndRemoveAllowed && (
              <Button
                data-testid="gds-vp-ss-ReAcquired-btn"
                disabled={dealStatus === 'Deactivated'}
                onClick={onReAcquireClick}
                variant="newOutlined"
              >
                {translate('VendorDeals.ReAcquired')}
              </Button>
            )}
            {allowQuoteGeneration && data?.bidstatus === 'A' && (
              <Button
                data-testid="gds-vp-ss-generate-full-quote"
                disabled={dealStatus === 'Deactivated'}
                onClick={() => setOpenPopup('bcnList')}
                variant="newOutlined"
              >
                {selected.length
                  ? translate('VendorDeals.generateQuote', {
                      count: selected.length,
                    })
                  : translate('VendorDeals.generateFullQuote')}
              </Button>
            )}
            {businessType !== 'Subscription' && (
              <Button
                data-testid="gds-vp-ss-download-order-history-btn"
                disabled={
                  !lines
                    .filter((l) => selected.includes(l.item_id))
                    .some((l) => l.dd_used_qty !== 0)
                }
                onClick={handleDownloadOrderHistory}
                variant="newOutlined"
              >
                {translate('VendorDeals.DownloadOrderHistory')}
              </Button>
            )}
            {businessType !== 'Subscription' && (
              <FormControl sx={{ minWidth: 200, width: '240px' }}>
                <DiscountLinesSearchBar
                  dealid={dealid}
                  importreferenceid={importreferenceid}
                  onClear={() => {
                    setVpn('');
                    setPagination({ ...pagination, page: 1 });
                  }}
                  onOptionSelected={(value) => {
                    setVpn(value);
                    setPagination({ ...pagination, page: 1 });
                  }}
                />
              </FormControl>
            )}
            {exception && businessType !== 'Subscription' && (
              <Button
                data-testid="gds-vp-ss-refresh-sku"
                disabled={!lines.length || dealStatus === 'Deactivated'}
                onClick={() => handleRefreshImSku()}
                startIcon={<SyncIcon />}
                sx={{ pointerEvents: 'visible' }}
                variant="newContained"
              >
                {translate('VendorProgram.RefreshImSku')}
              </Button>
            )}
            {exception && data?.showIncentivesHeader && (
              <Button
                data-testid="gds-vp-ss-SaveIncentive"
                onClick={() => {
                  setOpensaveincentivebtn(false);
                  setOpenPopup('saveIncentive');
                }}
                // startIcon={<SyncIcon />}
                sx={{ pointerEvents: 'visible' }}
                variant="newContained"
              >
                {translate('ExceptionDeals.SaveIncentive')}
              </Button>
            )}
            <Button
              data-testid="gds-vp-ss-tableSettings"
              onClick={() => setOpenTableSettings(true)}
              startIcon={<TuneIcon />}
              variant="newText"
            >
              {translate('Common.TableSettings')}
            </Button>
          </Stack>
        </Stack>
        <Table
          columns={renderedColumns}
          loading={isLoading}
          onRowClick={(row) =>
            row.productset_type !== 'BL' &&
            data?.bidstatus === 'A' &&
            handleRowClick?.(row)
          }
          paginationComponent={
            (totalCount > 10 || lines_total_pages > 1) && requestPagination()
          }
          rowId={(row) => row.item_id}
          rows={rows}
          selectedRows={selected}
          sx={{ ...pinnedColumnsStyle }}
        />
      </Stack>
      {openTableSettings && (
        <TableSetting
          close={setOpenTableSettings}
          columnGroups={sortedColumnGroups}
          setSortableList={setSortableList}
        />
      )}
      <QuantityValidationDialog
        error={quantityError}
        onClose={() => setQuantityError(false)}
      />

      {openPopup === 'bcnList' && (
        <ShowBCNListDialog
          BcnCameOnDeal={data?.BcnCameOnDeal ?? false}
          bcn_linking_level={String(data?.bcn_Linking_Level)}
          branchCodes={data?.lines[0]?.vd_customer_branch_number}
          customerSetId={String(customerset_id)}
          dealId={String(dealId)}
          discountType={data?.discounttype}
          generateQuote={handleGenerateQuote}
          handleClose={() => setOpenPopup(false)}
          isResolvedOnException={data?.is_resolved_on_exception ?? false}
          resellerAuth={String(data?.reseller_Authorization)}
          resellerName={String(data?.reseller_name)}
          vendor_program_id={data?.vendor_program_id}
        />
      )}
      {openPopup === 'saveIncentive' && (
        <ConfirmationDialog
          buttons={[
            {
              children: translate('Common.Cancel'),
              onClick: () => setOpenPopup(false),
              variant: 'newOutlined',
            },
            {
              children: translate('Common.Submit'),

              disabled: opensaveincentivebtn,
              onClick: () => {
                handleSaveIncentive();
              },
              variant: 'newContained',
            },
          ]}
          content={
            <Typography
              align="center"
              sx={{ width: '100%' }}
              variant="subtitle2"
            >
              {translate('ExceptionDeals.SaveIncentiveConfirmMessage')}
            </Typography>
          }
          data-testid="gds-vp-ss-SaveIncentivedisable"
          open={true}
          sx={{
            '& .MuiDialog-paper': {
              gap: 2,
              minWidth: '500px',
            },
            '& .MuiDialogTitle-root ': { lineHeight: 'normal' },
          }}
          title=""
        />
      )}
    </>
  );
}
